import { camelCase, get, isEmpty, isUndefined, toNumber } from 'lodash';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React, { FocusEventHandler, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { FB, FBTextFieldProps } from '..';
import { translate } from '../../../common/intl';
import { THRESHOLD_KEY } from '../../../state/ducks/documentRevisions/constants';
import { WO_LOT_ID_KEY_NAME } from '../../../state/ducks/documentRevisions/documentType/types';
import { FormActionsProvider, useFormActions } from '../../components/forms/FormActions';
import { DocTypeGroups } from '../../dashboard.new/line.items/common/types';
import { checkIsDocumentPOAM } from '../../documentRevision/helpers/checkDocumentGroup';
import { isTranslation } from '../../translations/types';
import { isLhrInvalid, LHRS_ACCEPTED_QUANTITY, LHRS_REJECTED_QUANTITY, LHRS_START_QUANTITY } from '../FBLHRStep/utils';

const LHRS_QUANTITY_FIELDS = [LHRS_START_QUANTITY, LHRS_ACCEPTED_QUANTITY, LHRS_REJECTED_QUANTITY];

export const withFBTextField = <T extends FBTextFieldProps>(
  Component: React.FC<T>,
): React.FC<T> => {
  const Comp = ({
    onFocus,
    multiline,
    rows = 1,
    placeholder = '',
    defaultValue,
    disabled,
    name = '',
    value,
    error,
    helperText,
    ...props
  }: T) => {
    const { formState, workspaceState } = FB.useStores();
    const { submitForm } = useFormActions();
    const { workspaceMode: mode } = formState ?? {};
    const { id } = workspaceState ?? {};
    const isDocumentPOAM = checkIsDocumentPOAM(workspaceState?.document?.document?.documentType?.groupOptions);
    const formValue = useObserver(() => {
      // getFieldValue is an untracked action; subscribe to edits and bulk value replacement explicitly.
      formState?.newValues.get(name);
      return get(formState?.values, name, formState?.getFieldValue(name));
    });
    defaultValue = defaultValue ?? formValue;

    if (!['justificationOfChange', 'descriptionOfChange'].includes(name)) {
      value = value ?? formValue ?? '';
    }

    const intl = useIntl();
    disabled = (
      disabled
      || get(props.hellosignFlowProps, 'type') === 'HS_DEFINED_FIELD'
      || formState?.getFieldValue('signatureRequestId')
    );

    if (name === WO_LOT_ID_KEY_NAME) {
      disabled = true;
    }

    (isUndefined(multiline)) && (
      multiline = Boolean(rows && (rows as number) > 1)
    );

    isTranslation(placeholder) && (placeholder = intl.formatMessage({ id: placeholder }));
    onFocus = () => {
      if (formState?.isBackdropOpen) { return; }
      if (!name) { return; }
      if (!id) { return; }
      formState?.lockField({
        fields: [{
          formInput: [{
            [name]: true,
          }],
        }],
        documentRevisionId: id,
      }, name);
    };

    const roundToStep = function <T = number | string>(value: T, rawStep: T, rawMin: T, rawMax: T) {
      const number = toNumber(value);
      const min = isEmpty(rawMin) ? Number.MIN_VALUE : toNumber(rawMin);
      const max = isEmpty(rawMax) ? Number.MAX_VALUE : toNumber(rawMax);
      const step = isEmpty(rawStep) ? false : toNumber(rawStep);
      const roundedNumber = (step && name !== 'editorConfig.taxRate') ? Math.round((number - min) / step) * step + min : number;

      return Math.min(
        max,
        Math.max(
          min,
          roundedNumber,
        ),
      );
    };

    const onBlur: FocusEventHandler<HTMLInputElement> = (ev) => {
      const { type, value, step, min, max } = ev.target;

      if (formState?.isBackdropOpen) {
        ev.target.focus();
        return;
      }
      if (!name) {
        return;
      }
      if (type === 'number' && !isEmpty(value) && (!isEmpty(step) || !isEmpty(min) || !isEmpty(max))) {
        const numericSteppedValue = roundToStep(value, step, min, max);
        formState?.setFieldValue(name, numericSteppedValue, true);
      }
      if (isDocumentPOAM && name === THRESHOLD_KEY && isEmpty(value)) {
        // Setting POAM Item form value back to 0 If the value is removed
        formState?.setFieldValue(name, 0, true);
      }

      if (name.includes('headquarterAddress')) {
        const siteInformation = workspaceState?.formInput?.siteInformation;
        const subFieldName = name.split('headquarterAddress')[1];
        const updatedSites = siteInformation?.map(site => {
          if (!site?.isSameAsHQ) {
            return site;
          }

          return {
            ...site,
            ...(subFieldName ? { [camelCase(subFieldName)]: value } : { siteAddress: value }),
          };
        });

        formState?.setFieldValue('siteInformation', updatedSites);
      }

      formState?.unlockField(name);
      formState?.setFieldAutosave(name);

      const isArItemField = name.includes('.justificationOfChange') || name.includes('.descriptionOfChange');
      if (workspaceState?.isChangeRequest && workspaceState?.autosave && isArItemField) {
        submitForm();
        return;
      }

      const isLhrStartField = name.includes(LHRS_START_QUANTITY);
      if (!workspaceState?.autosave && isLhrStartField) {
        workspaceState?.saveDocRev(formState?.getValues(), true);
      }
    };

    useEffect(() => reaction(
      () => workspaceState?.formInputSync.get(name) as string | undefined,
      (value?: string) => {
        const previousValue = formState?.getFieldValue(name);
        if (previousValue === value && formState?.getInputState(name)?.value === value) { return; }
        formState?.setFieldValue(name, value, true);
        if (previousValue !== value) {
          formState?.scheduleChangeValidation();
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), []);

    const isPIInstance = workspaceState?.document?.document.documentType.group === DocTypeGroups.PI_INSTANCE;
    const isLhrQuantityInvalid = useObserver(() => {
      if (!isPIInstance) { return false; }
      // The quantity rule spans sibling fields, and edits no longer rerender the whole form.
      LHRS_QUANTITY_FIELDS.forEach((field) => {
        formState?.newValues.get(field);
        get(formState?.values, field);
      });
      return isLhrInvalid(name, value, formState);
    });

    if (isPIInstance) {
      error = isLhrQuantityInvalid;
      helperText = error
        ? translate('lhr.materials.total.cannot.exceed.start.quantity')
        : '';
    }

    return Component({
      ...(props as T),
      onFocus,
      onBlur,
      multiline,
      placeholder,
      disabled,
      defaultValue,
      name,
      rows,
      mode,
      value,
      error,
      helperText,
    });
  };
  return (props: T) => (
    <FormActionsProvider>
      <Comp {...props} />
    </FormActionsProvider>
  );
};
